import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { getPortfolio, addPosition, updatePosition, swapPosition, getSwapHistory, removePosition, getPortfolioHistory, getAssetPriceAt, getAsset } from "../lib/api";
import { useCurrency } from "../lib/currency";
import { useI18n } from "../lib/i18n";
import { TradingChart } from "../components/TradingChart";
import { AssetAutocomplete } from "../components/AssetAutocomplete";
import { Plus, X, TrendingUp, TrendingDown, Briefcase, Wallet, ChevronDown, ChevronRight, ArrowRightLeft, History, Pencil, Check } from "lucide-react";
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from "recharts";
import { toast } from "sonner";

const fmtPct = (v) => (v >= 0 ? "+" : "") + v.toFixed(2) + "%";

const TIMEFRAMES = [
    { id: "1d", key: "days" },
    { id: "1wk", key: "weeks" },
    { id: "1mo", key: "months" },
    { id: "3mo", key: "quarters" },
];

// Stable references to avoid prop-identity churn in TradingChart
const CHART_DEFAULT_INDICATORS = { volume: false };
const CHART_TYPES = [
    { id: "candle", labelKey: "candle" },
    { id: "line", labelKey: "line" },
    { id: "area", labelKey: "area" },
];

export default function Portfolio() {
    const { t } = useI18n();
    const [data, setData] = useState({ positions: [], invested: 0, current_value: 0, pnl_abs: 0, pnl_pct: 0 });
    const [history, setHistory] = useState({ candles: [], fibonacci: null });
    const [loading, setLoading] = useState(true);
    const [chartType, setChartType] = useState("candle");
    const [timeframe, setTimeframe] = useState("1d");
    const [expandedSym, setExpandedSym] = useState(null);
    const [showForm, setShowForm] = useState(false);
    const [showSwap, setShowSwap] = useState(false);
    const [showHistory, setShowHistory] = useState(false);
    // Inline edit di un singolo lot (id | null) + draft dei campi modificati
    const [editingLotId, setEditingLotId] = useState(null);
    const [editDraft, setEditDraft] = useState({});
    const nav = useNavigate();
    const { format } = useCurrency();

    const startEditLot = (p) => {
        setEditingLotId(p.id);
        setEditDraft({
            quantity:     String(p.quantity ?? ""),
            buy_price:    String(p.buy_price ?? ""),
            buy_currency: p.buy_currency || "USD",
            buy_date:     p.buy_date ? p.buy_date.slice(0, 10) : "",
            fees:         String(p.fees ?? "0"),
        });
    };
    const cancelEditLot = () => { setEditingLotId(null); setEditDraft({}); };
    const saveEditLot = async (lotId) => {
        try {
            const payload = {};
            if (editDraft.quantity !== "")  payload.quantity  = parseFloat(editDraft.quantity);
            if (editDraft.buy_price !== "") payload.buy_price = parseFloat(editDraft.buy_price);
            if (editDraft.buy_currency)     payload.buy_currency = editDraft.buy_currency;
            if (editDraft.buy_date)         payload.buy_date  = editDraft.buy_date;
            if (editDraft.fees !== "")      payload.fees      = parseFloat(editDraft.fees);
            await updatePosition(lotId, payload);
            toast.success("Acquisto aggiornato");
            cancelEditLot();
            load();
        } catch (e) {
            toast.error("Errore aggiornamento: " + (e?.response?.data?.detail || e.message));
        }
    };

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [p, h] = await Promise.all([getPortfolio(), getPortfolioHistory(timeframe)]);
            setData(p);
            setHistory(h);
        } catch (e) {
            toast.error("Errore caricamento: " + e.message);
        } finally {
            setLoading(false);
        }
    }, [timeframe]);

    useEffect(() => { load(); }, [load]);

    // Aggregate positions by symbol → compute average cost basis (FX-converted invested / qty)
    const grouped = useMemo(() => {
        const map = new Map();
        for (const p of data.positions) {
            const k = p.symbol;
            if (!map.has(k)) {
                map.set(k, {
                    symbol: p.symbol,
                    name: p.name,
                    quantity: 0,
                    invested: 0,        // USD equivalent
                    current_value: 0,
                    fees_usd: 0,        // Somma commissioni USD del gruppo
                    current_price: p.current_price,
                    native_currency: p.native_currency || "USD",
                    children: [],
                });
            }
            const g = map.get(k);
            g.quantity += Number(p.quantity);
            g.invested += Number(p.invested);
            g.current_value += Number(p.current_value);
            g.fees_usd += Number(p.fees_usd || 0);
            g.current_price = p.current_price;
            g.native_currency = p.native_currency || g.native_currency;
            g.children.push(p);
        }
        return Array.from(map.values()).map(g => {
            const avg = g.quantity ? g.invested / g.quantity : 0;
            const pnlAbs = g.current_value - g.invested;
            const pnlPct = g.invested ? (pnlAbs / g.invested) * 100 : 0;
            return { ...g, avg_cost: avg, pnl_abs: pnlAbs, pnl_pct: pnlPct };
        }).sort((a, b) => b.current_value - a.current_value);
    }, [data.positions]);

    // % variation over the visible chart period
    const periodChange = useMemo(() => {
        const c = history.candles || [];
        if (c.length < 2) return { abs: 0, pct: 0 };
        const first = c[0].close, last = c[c.length - 1].close;
        return { abs: last - first, pct: first ? ((last - first) / first) * 100 : 0 };
    }, [history]);

    // Today's candle variation (close − open) — what the user asked for: "voglio anche
    // che si veda quanto è la percentuale della candela di oggi" on the portfolio chart.
    const todayChange = useMemo(() => {
        const c = history.candles || [];
        if (c.length === 0) return { abs: 0, pct: 0, date: null };
        const last = c[c.length - 1];
        const abs = last.close - last.open;
        const pct = last.open ? (abs / last.open) * 100 : 0;
        return { abs, pct, date: last.date };
    }, [history]);

    // Portfolio asset-allocation slices for the pie chart (one slice per ticker).
    // Uses the same color palette as Budget's expense pie for visual consistency.
    const portfolioPieData = useMemo(() => {
        const PALETTE = ["#22c55e", "#3b82f6", "#f97316", "#eab308", "#ec4899",
                         "#06b6d4", "#a855f7", "#ef4444", "#10b981", "#f59e0b"];
        const total = grouped.reduce((s, g) => s + g.current_value, 0);
        return grouped
            .filter(g => g.current_value > 0)
            .map((g, i) => ({
                name: g.symbol,
                full_name: g.name,
                value: g.current_value,
                color: PALETTE[i % PALETTE.length],
                pct: total ? (g.current_value / total) * 100 : 0,
                pnl_pct: g.pnl_pct,
            }));
    }, [grouped]);

    const handleRemove = async (id, symbol, e) => {
        e.stopPropagation();
        try {
            await removePosition(id);
            toast.success(`${symbol} ${t("removed") || "rimosso"}`);
            load();
        } catch (err) {
            toast.error("Errore: " + err.message);
        }
    };

    const handleRemoveGroup = async (group, e) => {
        e.stopPropagation();
        const n = group.children.length;
        const ok = window.confirm(
            n === 1
                ? `Rimuovere la posizione su ${group.symbol}?`
                : `Rimuovere TUTTE le ${n} posizioni su ${group.symbol}?`
        );
        if (!ok) return;
        try {
            await Promise.all(group.children.map(p => removePosition(p.id)));
            toast.success(`${group.symbol} · ${n} ${n === 1 ? "posizione rimossa" : "posizioni rimosse"}`);
            load();
        } catch (err) {
            toast.error("Errore: " + err.message);
        }
    };

    const totalUp = (data.pnl_abs || 0) >= 0;
    const periodUp = periodChange.pct >= 0;

    return (
        <div data-testid="portfolio-page" className="px-4 py-6 max-w-[1600px] mx-auto fade-up">
            <div className="flex items-end justify-between mb-6 flex-wrap gap-4">
                <div>
                    <div className="text-[10px] text-mute tracking-[0.2em] mb-1">PERSONAL PORTFOLIO</div>
                    <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight brand-gradient-text">
                        {t("portfolio_title_pre")} {t("portfolio_title_accent")}
                    </h1>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        data-testid="swap-history-btn"
                        onClick={() => { setShowHistory(s => !s); setShowSwap(false); setShowForm(false); }}
                        className="border px-3 py-2 text-xs font-semibold tracking-wider hover:bg-elev2 flex items-center gap-2 text-mute hover:text-foreground"
                        style={{ borderColor: "var(--border-soft)" }}
                        title="Storico swap"
                    >
                        <History size={14} />
                        STORICO
                    </button>
                    <button
                        data-testid="swap-position-btn"
                        onClick={() => { setShowSwap(s => !s); setShowForm(false); setShowHistory(false); }}
                        className="border px-4 py-2 text-xs font-semibold tracking-wider hover:bg-elev2 flex items-center gap-2"
                        style={{ color: "var(--brand-blue)", borderColor: "var(--brand-blue)" }}
                    >
                        <ArrowRightLeft size={14} />
                        {showSwap ? t("cancel") : "SWAP"}
                    </button>
                    <button
                        data-testid="add-position-btn"
                        onClick={() => { setShowForm(s => !s); setShowSwap(false); setShowHistory(false); }}
                        className="border px-4 py-2 text-xs font-semibold tracking-wider hover:bg-elev2 flex items-center gap-2"
                        style={{ color: "var(--amber)", borderColor: "var(--amber)" }}
                    >
                        <Plus size={14} />
                        {showForm ? t("cancel") : t("add_position")}
                    </button>
                </div>
            </div>

            {showForm && <PositionForm onSaved={() => { setShowForm(false); load(); }} />}
            {showSwap && <SwapForm grouped={grouped} onSaved={() => { setShowSwap(false); load(); }} />}
            {showHistory && <SwapHistoryPanel onClose={() => setShowHistory(false)} />}

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
                <StatBox label={t("current_value")} value={format(data.current_value)} icon={Wallet} accent />
                <StatBox label={t("invested")} value={format(data.invested)} icon={Briefcase} />
                <StatBox
                    label="Commissioni"
                    value={format(data.fees_total || 0)}
                    data-testid="stat-fees-total"
                />
                <StatBox label="P&L" value={(totalUp ? "+" : "-") + format(Math.abs(data.pnl_abs), { withSymbol: true })} highlight={totalUp ? "green" : "red"} />
                <StatBox label="P&L %" value={fmtPct(data.pnl_pct || 0)} highlight={totalUp ? "green" : "red"} icon={totalUp ? TrendingUp : TrendingDown} />
            </div>

            {/* Chart */}
            <div className="border border-soft bg-elev p-4 mb-4">
                <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                    <div className="flex items-center gap-3 flex-wrap">
                        <div className="text-xs text-mute tracking-wider">{t("portfolio_chart_title")}</div>
                        {history.candles && history.candles.length >= 2 && (
                            <div data-testid="period-change" className="flex items-center gap-1 text-xs tnum font-semibold"
                                style={{ color: periodUp ? "var(--green)" : "var(--red)" }}>
                                {periodUp ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
                                {periodUp ? "+" : ""}{format(periodChange.abs)} ({fmtPct(periodChange.pct)})
                            </div>
                        )}
                        {history.candles && history.candles.length > 0 && (
                            <div data-testid="today-change" className="flex items-center gap-1 text-[11px] tnum"
                                style={{ color: todayChange.pct >= 0 ? "var(--green)" : "var(--red)" }}
                                title={`Variazione candela del ${todayChange.date || ""}`}>
                                <span className="text-mute">Oggi:</span>
                                {todayChange.pct >= 0 ? "+" : ""}{format(todayChange.abs)} ({fmtPct(todayChange.pct)})
                            </div>
                        )}
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                        <div data-testid="portfolio-timeframe" className="flex gap-1">
                            {TIMEFRAMES.map(tf => (
                                <button
                                    key={tf.id}
                                    data-testid={`tf-${tf.id}`}
                                    onClick={() => setTimeframe(tf.id)}
                                    className="px-2 py-1 text-[10px] font-semibold border"
                                    style={timeframe === tf.id
                                        ? { borderColor: "var(--amber)", color: "var(--amber)", background: "rgba(212,175,55,0.10)" }
                                        : { borderColor: "transparent", color: "var(--text-dim)" }}
                                >
                                    {t(tf.key)}
                                </button>
                            ))}
                        </div>
                        <div className="flex gap-1 border border-soft p-0.5">
                            {[["candle", t("candle")], ["line", t("line")], ["area", t("area")]].map(([id, lbl]) => (
                                <button
                                    key={id}
                                    data-testid={`portfolio-chart-${id}`}
                                    onClick={() => setChartType(id)}
                                    className="px-3 py-1 text-[10px] font-semibold uppercase"
                                    style={chartType === id ? { background: "rgba(212,175,55,0.12)", color: "var(--amber)" } : { color: "var(--text-dim)" }}
                                >
                                    {lbl}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
                {history.candles && history.candles.length > 0 ? (
                    <TradingChart
                        candles={history.candles}
                        chartType={chartType}
                        height={380}
                        fibonacci={history.fibonacci || null}
                        defaultIndicators={CHART_DEFAULT_INDICATORS}
                    />
                ) : (
                    <div className="text-center py-20 text-mute text-sm">
                        {loading ? t("loading") : t("no_positions")}
                    </div>
                )}
                <div className="text-[10px] text-mute mt-2 tracking-wide">
                    Zoom: rotella o pinch · Trascina per scorrere · Le candele mostrano il valore totale aggregato del portafoglio
                </div>
            </div>

            {/* Asset allocation pie + breakdown */}
            {portfolioPieData.length > 0 && (
                <PortfolioAllocationPie data={portfolioPieData} format={format} totalValue={data.current_value} />
            )}

            {/* Holdings table — grouped by symbol with avg cost basis */}
            <div data-testid="holdings-table" className="border border-soft bg-elev">
                <div className="px-4 py-2.5 text-[10px] tracking-widest text-mute border-b border-soft uppercase flex items-center justify-between">
                    <span>{t("my_assets")} · {grouped.length}</span>
                </div>
                {loading && <div className="px-4 py-10 text-center text-mute text-sm">{t("loading")}</div>}
                {!loading && grouped.length === 0 && (
                    <div className="px-4 py-12 text-center text-mute text-sm">
                        {t("no_positions")}
                    </div>
                )}
                {!loading && grouped.length > 0 && (
                    <>
                        <div className="hidden md:grid grid-cols-12 px-4 py-2 text-[10px] tracking-widest text-mute border-b border-soft uppercase">
                            <div className="col-span-3">{t("symbol")}</div>
                            <div className="col-span-1 text-right">{t("quantity")}</div>
                            <div className="col-span-2 text-right">{t("invested")}</div>
                            <div className="col-span-1 text-right">{t("avg_cost")}</div>
                            <div className="col-span-1 text-right">Commissioni</div>
                            <div className="col-span-1 text-right">{t("current_value")}</div>
                            <div className="col-span-2 text-right">P&L</div>
                            <div className="col-span-1 text-right">·</div>
                        </div>
                        {grouped.map(g => {
                            const up = (g.pnl_pct || 0) >= 0;
                            const expanded = expandedSym === g.symbol;
                            const hasMultiple = g.children.length > 1;
                            return (
                                <div key={g.symbol} data-testid={`group-${g.symbol}`}>
                                    <div
                                        role="button"
                                        tabIndex={0}
                                        data-testid={`group-row-${g.symbol}`}
                                        onClick={() => hasMultiple ? setExpandedSym(expanded ? null : g.symbol) : nav(`/asset/${encodeURIComponent(g.symbol)}`)}
                                        onKeyDown={(e) => { if (e.key === "Enter") { hasMultiple ? setExpandedSym(expanded ? null : g.symbol) : nav(`/asset/${encodeURIComponent(g.symbol)}`); } }}
                                        className="w-full grid grid-cols-2 md:grid-cols-12 px-4 py-3 text-sm border-b border-soft items-center hover:bg-elev2 text-left gap-y-1 cursor-pointer"
                                    >
                                        <div className="col-span-2 md:col-span-3 flex items-center gap-2">
                                            {(() => {
                                                if (!hasMultiple) return <span className="w-[14px]" />;
                                                return expanded
                                                    ? <ChevronDown size={14} className="text-mute" />
                                                    : <ChevronRight size={14} className="text-mute" />;
                                            })()}
                                            <div>
                                                <div className="font-semibold flex items-center gap-1.5" style={{ color: "var(--amber)" }}>
                                                    {g.symbol}
                                                    {hasMultiple && <span className="text-[9px] text-mute">×{g.children.length}</span>}
                                                    <CurrencyBadge code={g.native_currency} />
                                                </div>
                                                <div className="text-mute text-[10px] truncate">{g.name}</div>
                                            </div>
                                        </div>
                                        <div className="col-span-1 md:col-span-1 text-right tnum text-xs">{g.quantity}</div>
                                        <div className="col-span-2 md:col-span-2 text-right tnum text-xs font-semibold" data-testid={`invested-${g.symbol}`}>{format(g.invested)}</div>
                                        <div className="col-span-1 md:col-span-1 text-right tnum text-xs">{format(g.avg_cost, { decimals: 4 })}</div>
                                        <div className="col-span-1 md:col-span-1 text-right tnum text-xs">{format(g.current_price)}</div>
                                        <div className="col-span-2 md:col-span-1 text-right tnum font-semibold">{format(g.current_value)}</div>
                                        <div className={`col-span-2 md:col-span-2 text-right tnum font-semibold ${up ? "text-green-up" : "text-red-down"}`}>
                                            <div>{up ? "+" : ""}{format(g.pnl_abs)}</div>
                                            <div className="text-[10px]">{fmtPct(g.pnl_pct)}</div>
                                        </div>
                                        <div className="col-span-2 md:col-span-1 flex justify-end">
                                            <button
                                                type="button"
                                                data-testid={`remove-group-${g.symbol}`}
                                                onClick={(e) => handleRemoveGroup(g, e)}
                                                title={hasMultiple ? `Rimuovi tutte le ${g.children.length} posizioni` : "Rimuovi"}
                                                className="p-1.5 border border-soft hover:border-red-down"
                                                style={{ color: "var(--red)" }}
                                            >
                                                <X size={14} />
                                            </button>
                                        </div>
                                    </div>
                                    {expanded && hasMultiple && (
                                        <div data-testid={`group-children-${g.symbol}`} className="bg-elev2 border-b border-soft">
                                            {g.children.map(p => {
                                                const cup = (p.pnl_pct || 0) >= 0;
                                                const isEditing = editingLotId === p.id;
                                                if (isEditing) {
                                                    // Layout allineato all'header: 3 (date) + 1 (qty) + 2 (invested-placeholder)
                                                    // + 1 (buy_price) + 1 (fees) + 1 (currency) + 2 (save/cancel) + 1 (spacer)
                                                    return (
                                                        <div key={p.id} className="grid grid-cols-12 px-4 py-2 text-xs items-center gap-y-1 gap-x-2 border-t border-soft bg-elev">
                                                            <div className="col-span-3 pl-6">
                                                                <input
                                                                    data-testid={`edit-buy-date-${p.id}`}
                                                                    type="date" value={editDraft.buy_date}
                                                                    max={new Date().toISOString().slice(0, 10)}
                                                                    onChange={e => setEditDraft(d => ({ ...d, buy_date: e.target.value }))}
                                                                    className="w-full bg-elev2 border border-soft px-1 py-1 text-xs tnum"
                                                                    style={{ colorScheme: "dark" }}
                                                                />
                                                            </div>
                                                            <div className="col-span-1">
                                                                <input
                                                                    data-testid={`edit-quantity-${p.id}`}
                                                                    type="number" step="any" min="0" value={editDraft.quantity}
                                                                    onChange={e => setEditDraft(d => ({ ...d, quantity: e.target.value }))}
                                                                    className="w-full bg-elev2 border border-soft px-1 py-1 text-xs tnum text-right"
                                                                />
                                                            </div>
                                                            {/* Colonna "invested" (col-span-2): calcolata, non editabile */}
                                                            <div className="col-span-2 text-right text-mute text-[10px] italic">
                                                                (auto)
                                                            </div>
                                                            <div className="col-span-1">
                                                                <input
                                                                    data-testid={`edit-buy-price-${p.id}`}
                                                                    type="number" step="any" min="0" value={editDraft.buy_price}
                                                                    onChange={e => setEditDraft(d => ({ ...d, buy_price: e.target.value }))}
                                                                    className="w-full bg-elev2 border border-soft px-1 py-1 text-xs tnum text-right"
                                                                    title="Prezzo di acquisto"
                                                                />
                                                            </div>
                                                            <div className="col-span-1">
                                                                <input
                                                                    data-testid={`edit-fees-${p.id}`}
                                                                    type="number" step="any" min="0" value={editDraft.fees}
                                                                    onChange={e => setEditDraft(d => ({ ...d, fees: e.target.value }))}
                                                                    className="w-full bg-elev2 border border-soft px-1 py-1 text-xs tnum text-right"
                                                                    title="Commissioni"
                                                                />
                                                            </div>
                                                            <div className="col-span-1">
                                                                <select
                                                                    data-testid={`edit-buy-currency-${p.id}`}
                                                                    value={editDraft.buy_currency}
                                                                    onChange={e => setEditDraft(d => ({ ...d, buy_currency: e.target.value }))}
                                                                    className="w-full bg-elev2 border border-soft px-1 py-1 text-[10px] tnum"
                                                                    title="Valuta"
                                                                >
                                                                    <option value="USD">USD</option>
                                                                    <option value="EUR">EUR</option>
                                                                    <option value="GBP">GBP</option>
                                                                    <option value="JPY">JPY</option>
                                                                    <option value="CHF">CHF</option>
                                                                </select>
                                                            </div>
                                                            <div className="col-span-2 flex justify-end gap-1">
                                                                <button
                                                                    data-testid={`save-edit-${p.id}`}
                                                                    onClick={() => saveEditLot(p.id)}
                                                                    className="p-1 border border-soft hover:bg-elev2"
                                                                    style={{ color: "var(--green)" }}
                                                                    title="Salva"
                                                                ><Check size={12} /></button>
                                                                <button
                                                                    data-testid={`cancel-edit-${p.id}`}
                                                                    onClick={cancelEditLot}
                                                                    className="p-1 border border-soft text-mute hover:bg-elev2"
                                                                    title="Annulla"
                                                                ><X size={12} /></button>
                                                            </div>
                                                            <div className="col-span-1" />
                                                            <div className="col-span-12 pl-6 text-[9px] text-mute italic">
                                                                Modifica qualsiasi campo e premi ✓ per salvare.
                                                            </div>
                                                        </div>
                                                    );
                                                }
                                                return (
                                                    <div key={p.id} className="grid grid-cols-12 px-4 py-2 text-xs items-center gap-y-1 border-t border-soft">
                                                        <div className="col-span-3 pl-6 text-mute">
                                                            <div>{p.buy_date?.slice(0, 10)}</div>
                                                            <div className="text-[9px]">id: {p.id?.slice(0, 8)}</div>
                                                        </div>
                                                        <div className="col-span-1 text-right tnum">{p.quantity}</div>
                                                        <div className="col-span-2 text-right tnum font-semibold" data-testid={`invested-lot-${p.id}`}>{format(p.invested)}</div>
                                                        <div className="col-span-1 text-right tnum">
                                                            {format(p.buy_price_usd ?? p.buy_price)}
                                                            {p.buy_currency && p.buy_currency !== "USD" && (
                                                                <div className="text-mute text-[9px]">({p.buy_price} {p.buy_currency})</div>
                                                            )}
                                                        </div>
                                                        <div className="col-span-1 text-right tnum" title="Commissioni pagate">
                                                            {(p.fees || 0) > 0 ? format(p.fees_usd ?? p.fees) : <span className="text-mute">—</span>}
                                                        </div>
                                                        <div className="col-span-1 text-right tnum">{format(p.current_value)}</div>
                                                        <div className={`col-span-2 text-right tnum ${cup ? "text-green-up" : "text-red-down"}`}>{fmtPct(p.pnl_pct)}</div>
                                                        <div className="col-span-1 flex justify-end gap-1">
                                                            <button
                                                                data-testid={`edit-position-${p.id}`}
                                                                onClick={() => startEditLot(p)}
                                                                className="text-mute hover:text-amber p-1"
                                                                title="Modifica"
                                                                style={{ color: "var(--amber)" }}
                                                            ><Pencil size={12} /></button>
                                                            <button
                                                                data-testid={`remove-position-${p.id}`}
                                                                onClick={(e) => handleRemove(p.id, p.symbol, e)}
                                                                className="text-mute hover:text-red-down p-1"
                                                            ><X size={12} /></button>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                            <div className="px-4 py-2 border-t border-soft flex justify-end">
                                                <button
                                                    onClick={(e) => { e.stopPropagation(); nav(`/asset/${encodeURIComponent(g.symbol)}`); }}
                                                    className="text-[10px] text-amber hover:underline"
                                                    style={{ color: "var(--amber)" }}
                                                >
                                                    Vai alla scheda asset →
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                    {!hasMultiple && null}
                                </div>
                            );
                        })}
                    </>
                )}
            </div>
        </div>
    );
}

// Tiny badge that shows the native listing currency of a ticker.
// Color-coded so a mixed portfolio (EUR/USD/GBP) is scannable at a glance.
const CURRENCY_BADGE_COLORS = {
    USD: { bg: "rgba(76, 175, 80, 0.12)", color: "#7cdb8a" },
    EUR: { bg: "rgba(33, 150, 243, 0.12)", color: "#7ab7ee" },
    GBP: { bg: "rgba(156, 39, 176, 0.14)", color: "#c08fd4" },
    GBX: { bg: "rgba(156, 39, 176, 0.14)", color: "#c08fd4" },
    JPY: { bg: "rgba(244, 67, 54, 0.12)", color: "#e88a82" },
    CHF: { bg: "rgba(255, 152, 0, 0.12)", color: "#eab272" },
    DKK: { bg: "rgba(255, 193, 7, 0.12)", color: "#e6c574" },
    CAD: { bg: "rgba(255, 87, 34, 0.12)", color: "#e69680" },
    AUD: { bg: "rgba(0, 188, 212, 0.12)", color: "#7dd0db" },
    HKD: { bg: "rgba(233, 30, 99, 0.12)", color: "#e08fac" },
    CNY: { bg: "rgba(255, 235, 59, 0.10)", color: "#e6d76c" },
};

const CurrencyBadge = ({ code }) => {
    if (!code) return null;
    const label = code === "GBX" ? "GBp" : code;
    const palette = CURRENCY_BADGE_COLORS[code] || { bg: "rgba(255,255,255,0.06)", color: "var(--text-dim)" };
    return (
        <span
            data-testid={`currency-badge-${code}`}
            className="text-[9px] font-semibold tracking-wider px-1.5 py-[1px] rounded-sm"
            style={{ background: palette.bg, color: palette.color, letterSpacing: "0.05em" }}
            title={`Quotato in ${label}`}
        >
            {label}
        </span>
    );
};

const StatBox = ({ label, value, prefix, highlight, accent, icon: Icon, ...rest }) => {
    let color;
    if (highlight === "green") color = "var(--green)";
    else if (highlight === "red") color = "var(--red)";
    else if (accent) color = "var(--amber)";
    return (
        <div className="border border-soft bg-elev p-3" {...rest}>
            <div className="flex items-center justify-between text-[10px] text-mute tracking-widest">
                <span>{label}</span>
                {Icon && <Icon size={12} style={color ? { color } : undefined} />}
            </div>
            <div className="text-2xl font-bold tnum mt-1" style={color ? { color } : undefined}>
                {prefix}{value}
            </div>
        </div>
    );
};

// Custom tooltip identical-spirit to Budget pie: dark bg, light text, shows
// dollar value + share of portfolio. Reusable for any allocation pie.
const PORTFOLIO_PIE_TOOLTIP_STYLE = {
    background: "#0a0e14",
    border: "1px solid #2a3548",
    fontSize: 11,
    fontFamily: "JetBrains Mono, monospace",
    color: "#e5e7eb",
    padding: "8px 10px",
    borderRadius: 4,
    boxShadow: "0 4px 16px rgba(0,0,0,0.4)",
};

function PortfolioPieTooltip({ active, payload, format }) {
    if (!active || !payload?.length) return null;
    const p = payload[0].payload;
    const pnlGood = p.pnl_pct >= 0;
    return (
        <div style={PORTFOLIO_PIE_TOOLTIP_STYLE} data-testid="portfolio-pie-tooltip">
            <div style={{ color: "#9ca3af", marginBottom: 4, fontSize: 10, letterSpacing: "0.05em", textTransform: "uppercase" }}>
                {p.name}{p.full_name && <span style={{ marginLeft: 4, textTransform: "none" }}>· {p.full_name}</span>}
            </div>
            <div style={{ color: "#e5e7eb", padding: "1px 0" }}>
                <span style={{ color: p.color, marginRight: 6 }}>●</span>
                <span className="tnum">{format(p.value)}</span>
            </div>
            <div style={{ color: "#9ca3af", padding: "1px 0" }}>
                <span className="tnum">{p.pct.toFixed(1)}%</span> del portafoglio · <span className="tnum" style={{ color: pnlGood ? "#22c55e" : "#ef4444" }}>{pnlGood ? "+" : ""}{p.pnl_pct.toFixed(2)}%</span> P&L
            </div>
        </div>
    );
}

const PortfolioAllocationPie = React.memo(({ data, format, totalValue }) => (
    <div data-testid="portfolio-allocation-pie" className="border border-soft bg-elev p-4 mb-4">
        <div className="text-xs text-mute tracking-widest mb-3 flex items-center gap-2">
            <Briefcase size={12} />
            DISTRIBUZIONE PORTAFOGLIO
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="md:col-span-1" style={{ width: "100%", height: 260 }}>
                <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                        <Pie
                            data={data}
                            dataKey="value"
                            nameKey="name"
                            innerRadius={55}
                            outerRadius={100}
                            paddingAngle={2}
                            stroke="#0a0e14"
                            isAnimationActive={false}
                        >
                            {data.map((d) => <Cell key={d.name} fill={d.color} />)}
                        </Pie>
                        <Tooltip
                            content={<PortfolioPieTooltip format={format} />}
                            cursor={{ fill: "rgba(255,255,255,0.04)" }}
                        />
                    </PieChart>
                </ResponsiveContainer>
            </div>
            <div className="md:col-span-2">
                <div className="text-[10px] text-mute tracking-wider mb-2">VALORE TOTALE</div>
                <div className="text-2xl font-bold tnum mb-3" style={{ color: "var(--amber)" }}>{format(totalValue)}</div>
                <div className="grid grid-cols-2 gap-x-4 gap-y-1.5">
                    {data.map(d => (
                        <div key={d.name} className="flex items-center justify-between text-xs gap-2">
                            <div className="flex items-center gap-2 min-w-0">
                                <span className="w-2 h-2 flex-shrink-0" style={{ background: d.color }} />
                                <span className="text-dim truncate font-semibold">{d.name}</span>
                            </div>
                            <span className="text-mute tnum">{d.pct.toFixed(1)}%</span>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    </div>
));
PortfolioAllocationPie.displayName = "PortfolioAllocationPie";


const PositionForm = ({ onSaved }) => {
    const { t } = useI18n();
    const { currency: defaultCurrency, currencies } = useCurrency();
    const [form, setForm] = useState({
        symbol: "",
        name: "",
        quantity: "",
        buy_price: "",
        buy_currency: defaultCurrency,
        buy_date: new Date().toISOString().slice(0, 10),
        fees: "",
    });
    const [submitting, setSubmitting] = useState(false);
    const [priceLoading, setPriceLoading] = useState(false);
    const [priceInfo, setPriceInfo] = useState(null); // {actual_date, source: "auto"|"manual"}
    const userEditedPriceRef = useRef(false);
    const lastFetchKey = useRef("");

    // Auto-fetch historical price when symbol + date are set, only if user hasn't typed a manual price
    useEffect(() => {
        const sym = form.symbol.trim().toUpperCase();
        const dt = form.buy_date;
        if (!sym || !dt) return;
        if (userEditedPriceRef.current) return;
        const key = `${sym}|${dt}`;
        if (key === lastFetchKey.current) return;
        lastFetchKey.current = key;

        let cancelled = false;
        const handle = setTimeout(async () => {
            setPriceLoading(true);
            try {
                const r = await getAssetPriceAt(sym, dt);
                if (cancelled || userEditedPriceRef.current) return;
                setForm(f => ({
                    ...f,
                    buy_price: String(r.price),
                    buy_currency: r.currency || f.buy_currency,
                }));
                setPriceInfo({ actual_date: r.actual_date, source: "auto" });
            } catch (_e) {
                if (!cancelled) setPriceInfo({ error: true });
            } finally {
                if (!cancelled) setPriceLoading(false);
            }
        }, 400);
        return () => { cancelled = true; clearTimeout(handle); };
    }, [form.symbol, form.buy_date]);

    const onPriceChange = (val) => {
        userEditedPriceRef.current = true;
        setForm({ ...form, buy_price: val });
        setPriceInfo({ source: "manual" });
    };

    const submit = async (e) => {
        e.preventDefault();
        if (!form.symbol || !form.quantity || !form.buy_price || !form.buy_date) {
            toast.error("Compila tutti i campi obbligatori");
            return;
        }
        setSubmitting(true);
        try {
            await addPosition({
                symbol: form.symbol.toUpperCase().trim(),
                name: form.name || form.symbol.toUpperCase(),
                quantity: parseFloat(form.quantity),
                buy_price: parseFloat(form.buy_price),
                buy_currency: form.buy_currency,
                buy_date: form.buy_date,
                fees: form.fees === "" ? 0 : parseFloat(form.fees),
            });
            toast.success("Posizione aggiunta");
            onSaved();
        } catch (err) {
            toast.error("Errore: " + (err?.response?.data?.detail || err.message));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <form onSubmit={submit} data-testid="position-form" className="border bg-elev p-4 mb-4 grid grid-cols-1 md:grid-cols-8 gap-3" style={{ borderColor: "var(--amber)" }}>
            <div className="md:col-span-1">
                <label className="text-[10px] text-mute tracking-widest block mb-1">{t("symbol")} *</label>
                <AssetAutocomplete
                    value={form.symbol}
                    onChange={(v) => { setForm(f => ({ ...f, symbol: v })); userEditedPriceRef.current = false; }}
                    onPick={(item) => { setForm(f => ({ ...f, symbol: item.symbol, name: item.name })); userEditedPriceRef.current = false; }}
                    placeholder="AAPL, BTC-USD…"
                />
            </div>
            <div className="md:col-span-2">
                <label className="text-[10px] text-mute tracking-widest block mb-1">{t("name")}</label>
                <input
                    data-testid="form-name"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="Apple Inc."
                    className="w-full bg-elev2 border border-soft px-2 py-1.5 text-sm focus:border-amber outline-none"
                />
            </div>
            <div className="md:col-span-1">
                <label className="text-[10px] text-mute tracking-widest block mb-1">{t("quantity")} *</label>
                <input
                    data-testid="form-quantity"
                    type="number" step="any" min="0"
                    value={form.quantity}
                    onChange={(e) => setForm({ ...form, quantity: e.target.value })}
                    placeholder="10"
                    className="w-full bg-elev2 border border-soft px-2 py-1.5 text-sm tnum focus:border-amber outline-none"
                />
            </div>
            <div className="md:col-span-1">
                <label className="text-[10px] text-mute tracking-widest block mb-1 flex items-center justify-between">
                    <span>{t("buy_price")} *</span>
                    {priceLoading && <span className="text-[9px]" style={{ color: "var(--amber)" }}>…</span>}
                </label>
                <input
                    data-testid="form-price"
                    type="number" step="any" min="0"
                    value={form.buy_price}
                    onChange={(e) => onPriceChange(e.target.value)}
                    placeholder="150.00"
                    className="w-full bg-elev2 border border-soft px-2 py-1.5 text-sm tnum focus:border-amber outline-none"
                />
                {priceInfo?.source === "auto" && (
                    <div data-testid="price-auto-info" className="text-[9px] mt-0.5" style={{ color: "var(--green)" }}>
                        ✓ Prezzo storico {priceInfo.actual_date}
                    </div>
                )}
                {priceInfo?.error && (
                    <div className="text-[9px] mt-0.5 text-red-down">
                        Prezzo storico non disponibile
                    </div>
                )}
            </div>
            <div className="md:col-span-1">
                <label className="text-[10px] text-mute tracking-widest block mb-1">{t("currency")} *</label>
                <select
                    data-testid="form-currency"
                    value={form.buy_currency}
                    onChange={(e) => setForm({ ...form, buy_currency: e.target.value })}
                    className="w-full bg-elev2 border border-soft px-2 py-1.5 text-sm focus:border-amber outline-none"
                    style={{ colorScheme: "dark" }}
                >
                    {Object.entries(currencies).map(([code, info]) => (
                        <option key={code} value={code}>{info.symbol} {code}</option>
                    ))}
                </select>
            </div>
            <div className="md:col-span-1">
                <label className="text-[10px] text-mute tracking-widest block mb-1">{t("buy_date")} *</label>
                <input
                    data-testid="form-date"
                    type="date"
                    value={form.buy_date}
                    onChange={(e) => { setForm({ ...form, buy_date: e.target.value }); userEditedPriceRef.current = false; }}
                    max={new Date().toISOString().slice(0, 10)}
                    className="w-full bg-elev2 border border-soft px-2 py-1.5 text-sm tnum focus:border-amber outline-none"
                    style={{ colorScheme: "dark" }}
                />
            </div>
            <div className="md:col-span-1">
                <label className="text-[10px] text-mute tracking-widest block mb-1">
                    Commissioni
                </label>
                <input
                    data-testid="form-fees"
                    type="number" step="any" min="0"
                    value={form.fees}
                    onChange={(e) => setForm({ ...form, fees: e.target.value })}
                    placeholder="0.00"
                    title="Commissione pagata al broker per questo acquisto"
                    className="w-full bg-elev2 border border-soft px-2 py-1.5 text-sm tnum focus:border-amber outline-none"
                />
            </div>
            <div className="md:col-span-8 flex justify-end pt-1">
                <button
                    data-testid="form-submit"
                    type="submit"
                    disabled={submitting}
                    className="border px-4 py-1.5 text-xs font-semibold tracking-wider disabled:opacity-50"
                    style={{ background: "var(--amber)", color: "#000", borderColor: "var(--amber)" }}
                >
                    {submitting ? t("saving") : t("save_position")}
                </button>
            </div>
        </form>
    );
};


// ---------------------- Swap form ----------------------

/**
 * Swap form: convert qty of an OWNED asset (FROM) into a TARGET asset (TO),
 * preserving notional USD value. The price fields auto-populate from the live
 * markets endpoint but remain editable so the user can swap at a custom rate.
 *
 * The swap does NOT introduce any new portfolio fields — it works entirely on
 * existing PositionAdd shape via the /api/portfolio/swap endpoint.
 */
const SwapForm = ({ grouped, onSaved }) => {
    const { t } = useI18n();
    const today = new Date().toISOString().slice(0, 10);
    const ownedSymbols = (grouped || []).filter(g => g.quantity > 0);

    const [fromSymbol, setFromSymbol] = useState(ownedSymbols[0]?.symbol || "");
    const [fromQty, setFromQty] = useState("");
    const [fromPrice, setFromPrice] = useState("");
    const [toSymbol, setToSymbol] = useState("");
    const [toName, setToName] = useState("");
    const [toPrice, setToPrice] = useState("");
    const [swapDate, setSwapDate] = useState(today);
    const [submitting, setSubmitting] = useState(false);
    const [pricesLoading, setPricesLoading] = useState(false);

    // Auto-fetch FROM live price when symbol changes
    useEffect(() => {
        if (!fromSymbol) return;
        let cancelled = false;
        setPricesLoading(true);
        getAsset(fromSymbol)
            .then(d => { if (!cancelled && d?.price != null) setFromPrice(String(d.price)); })
            .catch(() => {})
            .finally(() => !cancelled && setPricesLoading(false));
        return () => { cancelled = true; };
    }, [fromSymbol]);

    // Auto-fetch TO live price when symbol changes
    useEffect(() => {
        if (!toSymbol) return;
        let cancelled = false;
        setPricesLoading(true);
        getAsset(toSymbol)
            .then(d => {
                if (cancelled) return;
                if (d?.price != null) setToPrice(String(d.price));
                if (d?.name && !toName) setToName(d.name);
            })
            .catch(() => {})
            .finally(() => !cancelled && setPricesLoading(false));
        return () => { cancelled = true; };
    // toName only matters at initial fetch — intentionally omit it to avoid loops
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [toSymbol]);

    const fromOwned = useMemo(
        () => ownedSymbols.find(g => g.symbol === fromSymbol)?.quantity || 0,
        [ownedSymbols, fromSymbol]
    );
    const notional = useMemo(() => {
        const q = parseFloat(fromQty); const p = parseFloat(fromPrice);
        return q > 0 && p > 0 ? q * p : 0;
    }, [fromQty, fromPrice]);
    const toReceive = useMemo(() => {
        const p = parseFloat(toPrice);
        return notional > 0 && p > 0 ? notional / p : 0;
    }, [notional, toPrice]);

    const setAll = () => setFromQty(String(fromOwned));

    const submit = async (e) => {
        e.preventDefault();
        if (!fromSymbol || !toSymbol || !fromQty || !fromPrice || !toPrice || !swapDate) {
            toast.error("Compila tutti i campi obbligatori");
            return;
        }
        if (fromSymbol === toSymbol) {
            toast.error("Da e A devono essere simboli diversi");
            return;
        }
        const q = parseFloat(fromQty);
        if (q > fromOwned + 1e-9) {
            toast.error(`Quantità eccessiva: possiedi ${fromOwned} ${fromSymbol}`);
            return;
        }
        setSubmitting(true);
        try {
            const res = await swapPosition({
                from_symbol: fromSymbol,
                from_quantity: q,
                from_price: parseFloat(fromPrice),
                from_currency: "USD",
                to_symbol: toSymbol.toUpperCase().trim(),
                to_name: toName || toSymbol.toUpperCase(),
                to_price: parseFloat(toPrice),
                to_currency: "USD",
                swap_date: swapDate,
            });
            toast.success(`Swap eseguito: +${res.to.quantity_received.toFixed(6)} ${res.to.symbol}`);
            onSaved();
        } catch (err) {
            toast.error("Errore: " + (err?.response?.data?.detail || err.message));
        } finally {
            setSubmitting(false);
        }
    };

    if (ownedSymbols.length === 0) {
        return (
            <div data-testid="swap-empty" className="border bg-elev p-4 mb-4 text-sm text-mute"
                 style={{ borderColor: "var(--brand-blue)" }}>
                Per fare uno swap devi prima possedere almeno un asset nel portafoglio.
            </div>
        );
    }

    return (
        <form
            onSubmit={submit}
            data-testid="swap-form"
            className="border bg-elev p-4 mb-4 grid grid-cols-1 md:grid-cols-12 gap-3"
            style={{ borderColor: "var(--brand-blue)" }}
        >
            {/* FROM side */}
            <div className="md:col-span-5 border-r-0 md:border-r border-soft pr-0 md:pr-3">
                <div className="text-[10px] tracking-widest mb-2" style={{ color: "var(--red)" }}>DA (VENDI)</div>
                <div className="space-y-2">
                    <div>
                        <label className="text-[10px] text-mute tracking-widest block mb-1">Simbolo posseduto *</label>
                        <select
                            data-testid="swap-from-symbol"
                            value={fromSymbol}
                            onChange={(e) => setFromSymbol(e.target.value)}
                            className="w-full bg-elev2 border border-soft px-2 py-1.5 text-sm focus:border-amber outline-none"
                        >
                            {ownedSymbols.map(g => (
                                <option key={g.symbol} value={g.symbol}>
                                    {g.symbol} — {g.quantity.toFixed(6)} disponibili
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                        <div>
                            <label className="text-[10px] text-mute tracking-widest mb-1 flex items-center justify-between">
                                <span>Quantità *</span>
                                <button type="button" onClick={setAll} className="text-[9px] tracking-wider"
                                    style={{ color: "var(--brand-blue)" }} data-testid="swap-max-btn">
                                    MAX
                                </button>
                            </label>
                            <input
                                data-testid="swap-from-qty"
                                type="number" step="any" min="0"
                                value={fromQty}
                                onChange={(e) => setFromQty(e.target.value)}
                                placeholder={String(fromOwned)}
                                className="w-full bg-elev2 border border-soft px-2 py-1.5 text-sm focus:border-amber outline-none tnum"
                            />
                        </div>
                        <div>
                            <label className="text-[10px] text-mute tracking-widest block mb-1">Prezzo unitario (USD) *</label>
                            <input
                                data-testid="swap-from-price"
                                type="number" step="any" min="0"
                                value={fromPrice}
                                onChange={(e) => setFromPrice(e.target.value)}
                                className="w-full bg-elev2 border border-soft px-2 py-1.5 text-sm focus:border-amber outline-none tnum"
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* Center arrow */}
            <div className="md:col-span-1 flex md:flex-col items-center justify-center text-mute">
                <ArrowRightLeft size={20} style={{ color: "var(--brand-blue)" }} />
            </div>

            {/* TO side */}
            <div className="md:col-span-6">
                <div className="text-[10px] tracking-widest mb-2" style={{ color: "var(--green)" }}>A (COMPRA)</div>
                <div className="space-y-2">
                    <div>
                        <label className="text-[10px] text-mute tracking-widest block mb-1">Simbolo target *</label>
                        <AssetAutocomplete
                            value={toSymbol}
                            onChange={(v) => setToSymbol(v)}
                            onPick={(item) => { setToSymbol(item.symbol); setToName(item.name || ""); }}
                            placeholder="BTC-USD, ETH-USD…"
                        />
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                        <div>
                            <label className="text-[10px] text-mute tracking-widest block mb-1">Nome</label>
                            <input
                                value={toName}
                                onChange={(e) => setToName(e.target.value)}
                                placeholder="Bitcoin"
                                className="w-full bg-elev2 border border-soft px-2 py-1.5 text-sm focus:border-amber outline-none"
                            />
                        </div>
                        <div>
                            <label className="text-[10px] text-mute tracking-widest block mb-1">Prezzo (USD) *</label>
                            <input
                                data-testid="swap-to-price"
                                type="number" step="any" min="0"
                                value={toPrice}
                                onChange={(e) => setToPrice(e.target.value)}
                                className="w-full bg-elev2 border border-soft px-2 py-1.5 text-sm focus:border-amber outline-none tnum"
                            />
                        </div>
                        <div>
                            <label className="text-[10px] text-mute tracking-widest block mb-1">Data swap *</label>
                            <input
                                type="date"
                                value={swapDate}
                                onChange={(e) => setSwapDate(e.target.value)}
                                className="w-full bg-elev2 border border-soft px-2 py-1.5 text-sm focus:border-amber outline-none tnum"
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* Preview + submit */}
            <div className="md:col-span-12 flex flex-wrap items-center justify-between gap-3 pt-2 border-t border-soft">
                <div className="text-[11px] text-mute" data-testid="swap-preview">
                    {notional > 0 ? (
                        <>
                            Notional: <span className="tnum text-dim font-semibold">${notional.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                            {toPrice > 0 && (
                                <>
                                    {" · "}Riceverai: <span className="tnum font-semibold" style={{ color: "var(--green)" }}>
                                        {toReceive.toLocaleString("en-US", { minimumFractionDigits: 4, maximumFractionDigits: 8 })} {toSymbol || "—"}
                                    </span>
                                </>
                            )}
                        </>
                    ) : (
                        pricesLoading ? "Caricamento prezzi…" : "Compila i campi per vedere l'anteprima"
                    )}
                </div>
                <button
                    type="submit"
                    disabled={submitting || notional <= 0 || toReceive <= 0}
                    data-testid="swap-submit"
                    className="border px-4 py-1.5 text-xs font-semibold tracking-wider disabled:opacity-50"
                    style={{ background: "var(--brand-blue)", color: "#000", borderColor: "var(--brand-blue)" }}
                >
                    {submitting ? t("saving") : "ESEGUI SWAP"}
                </button>
            </div>
        </form>
    );
};

// ---------------------- Swap history panel ----------------------

/**
 * Read-only panel showing the audit trail of all swaps the user has performed.
 * Sourced from /api/portfolio/swap/history. Useful for tax records and
 * trade journalling.
 */
const SwapHistoryPanel = ({ onClose }) => {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        getSwapHistory()
            .then(d => { if (!cancelled) setItems(d.items || []); })
            .catch(() => !cancelled && setItems([]))
            .finally(() => !cancelled && setLoading(false));
        return () => { cancelled = true; };
    }, []);

    const fmtDate = (iso) => {
        if (!iso) return "—";
        try { return new Date(iso).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" }); }
        catch { return iso; }
    };
    const fmtNum = (n, dp = 6) => n == null
        ? "—"
        : Number(n).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: dp });

    return (
        <div className="border bg-elev p-4 mb-4" data-testid="swap-history-panel" style={{ borderColor: "var(--border-soft)" }}>
            <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                    <History size={14} className="text-mute" />
                    <span className="text-[11px] tracking-widest text-mute font-semibold">STORICO SWAP</span>
                    {!loading && <span className="text-[10px] text-mute">· {items.length} operazioni</span>}
                </div>
                <button onClick={onClose} className="text-mute hover:text-foreground" data-testid="history-close">
                    <X size={14} />
                </button>
            </div>

            {loading && <div className="text-xs text-mute">Caricamento…</div>}

            {!loading && items.length === 0 && (
                <div className="text-xs text-mute py-4 text-center" data-testid="history-empty">
                    Nessuno swap ancora effettuato. Quando ne esegui uno, lo trovi qui.
                </div>
            )}

            {!loading && items.length > 0 && (
                <div className="overflow-x-auto" data-testid="history-table">
                    <table className="w-full text-[11px] tnum">
                        <thead className="text-[9px] tracking-widest text-mute border-b border-soft">
                            <tr>
                                <th className="text-left px-2 py-2">DATA</th>
                                <th className="text-left px-2 py-2">DA</th>
                                <th className="text-right px-2 py-2">QTY VENDUTA</th>
                                <th className="text-right px-2 py-2">PREZZO</th>
                                <th className="text-right px-2 py-2">NOTIONAL</th>
                                <th className="text-left px-2 py-2">A</th>
                                <th className="text-right px-2 py-2">QTY RICEVUTA</th>
                                <th className="text-right px-2 py-2">PREZZO</th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map(h => (
                                <tr key={h.id} className="border-b border-soft hover:bg-elev2">
                                    <td className="px-2 py-2 text-mute">{fmtDate(h.created_at)}</td>
                                    <td className="px-2 py-2 font-semibold" style={{ color: "var(--red)" }}>{h.from_symbol}</td>
                                    <td className="text-right px-2 py-2">{fmtNum(h.from_quantity, 8)}</td>
                                    <td className="text-right px-2 py-2">${fmtNum(h.from_price, 4)}</td>
                                    <td className="text-right px-2 py-2 font-semibold text-dim">${fmtNum(h.notional_usd, 2)}</td>
                                    <td className="px-2 py-2 font-semibold" style={{ color: "var(--green)" }}>{h.to_symbol}</td>
                                    <td className="text-right px-2 py-2">{fmtNum(h.to_quantity, 8)}</td>
                                    <td className="text-right px-2 py-2">${fmtNum(h.to_price, 4)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

